import { translateFn } from '../../utils/translator.helper';

export const DEFAULT_ITEMS_PER_PAGE = 4;
export const DEFAULT_NUMBER_OF_ROWS = 2;
export const DEFAULT_GAP = 32;
export const MIN_CARD_SIZE_WIDTH = 400;

export enum GRID_TYPES {
  AVAILABLE = 'available',
  INSTALLED = 'installed',
}

export enum CARD_TYPES {
  READ_ONLY = 0,
  DEEP_LINK = 1,
  CONFIGURABLE = 2,
}

export const INTEGRATION_TYPES = {
  INTEGRATION_TYPE_VMWARE: 1,
  INTEGRATION_TYPE_VEEAM: 2,
  INTEGRATION_TYPE_MICROSOFT_ACTIVE_DIRECTORY: 3,
  INTEGRATION_TYPE_VMWARE_TANZU: 4,
  INTEGRATION_TYPE_MICROSOFT_EXCHANGE: 5,
  INTEGRATION_TYPE_SECURITY_TRAINING: 6,
  INTEGRATION_TYPE_CONTAINER_IMAGE_SCANNER: 7,
};

export const INTEGRATION_CARD_TYPE = {
  [INTEGRATION_TYPES.INTEGRATION_TYPE_VMWARE]: CARD_TYPES.CONFIGURABLE,
  [INTEGRATION_TYPES.INTEGRATION_TYPE_VEEAM]: CARD_TYPES.READ_ONLY,
  [INTEGRATION_TYPES.INTEGRATION_TYPE_MICROSOFT_ACTIVE_DIRECTORY]: CARD_TYPES.READ_ONLY,
  [INTEGRATION_TYPES.INTEGRATION_TYPE_VMWARE_TANZU]: CARD_TYPES.READ_ONLY,
  [INTEGRATION_TYPES.INTEGRATION_TYPE_MICROSOFT_EXCHANGE]: CARD_TYPES.READ_ONLY,
  [INTEGRATION_TYPES.INTEGRATION_TYPE_SECURITY_TRAINING]: CARD_TYPES.READ_ONLY,
  [INTEGRATION_TYPES.INTEGRATION_TYPE_CONTAINER_IMAGE_SCANNER]: CARD_TYPES.CONFIGURABLE,
};

export const INTEGRATIONS_DOCUMENTATION_LINK =
  'https://www.bitdefender.com/business/support/en/77211-1123675-integrations-hub.html';

function getIntegrationsDataSchema() {
  return {
    [INTEGRATION_TYPES.INTEGRATION_TYPE_VMWARE]: {
      image: 'vmware-logo.png',
      name: translateFn('VMware vCenter'),
      description: translateFn(
        'Automatically import and synchronize virtual machines from VMware vCenter into the GravityZone console, and have them displayed in the Network section.'
      ),
      linkLabel: {
        CLOUD: 'https://www.bitdefender.com/business/support/en/77209-1124910-vmware-vcenter.html',
        ON_PREMISE: 'N/A',
      },
    },
    [INTEGRATION_TYPES.INTEGRATION_TYPE_VEEAM]: {
      image: 'veeam-logo.png',
      name: translateFn('Veeam Backup & Replication'),
      description: translateFn(
        'Discovers security issues on virtual machines before they are restored to the production environment. This integration enhances data recovery, ensuring that restored machines are safe and secure.'
      ),
      linkLabel: {
        CLOUD: 'https://www.bitdefender.com/business/support/en/77209-158584-veeam-backup---replication.html',
        ON_PREMISE: 'https://www.bitdefender.com/business/support/en/77212-158584-veeam-backup---replication.html',
      },
    },
    [INTEGRATION_TYPES.INTEGRATION_TYPE_MICROSOFT_ACTIVE_DIRECTORY]: {
      image: 'microsoft-logo.png',
      name: translateFn('Microsoft Active Directory'),
      description: translateFn(
        'Enables GravityZone to import inventories from on-premises Active Directory. The integration allows you to easily deploy and manage protection on AD endpoints.'
      ),
      linkLabel: {
        CLOUD:
          'https://www.bitdefender.com/business/support/en/77209-158550-microsoft-active-directory.html#UUID-70ee7489-b3e1-b2e8-8297-a1e72486dd30',
        ON_PREMISE: 'N/A',
      },
    },
    [INTEGRATION_TYPES.INTEGRATION_TYPE_VMWARE_TANZU]: {
      image: 'vmware-logo.png',
      name: translateFn('VMware Tanzu'),
      description: translateFn(
        'Deploy BEST agents configured in the GravityZone console to BOSH-managed VM instances, integrating award-winning endpoint security during their creation within your VMware Tanzu environment.'
      ),
      linkLabel: {
        CLOUD: 'https://www.bitdefender.com/business/support/en/77209-158543-vmware-tanzu.html',
        ON_PREMISE: 'https://www.bitdefender.com/business/support/en/77212-158543-vmware-tanzu.html',
      },
    },
    [INTEGRATION_TYPES.INTEGRATION_TYPE_MICROSOFT_EXCHANGE]: {
      image: 'microsoft-logo.png',
      name: translateFn('Microsoft Exchange (on-premises)'),
      description: translateFn(
        'Extends GravityZone protection to your Exchange Servers, providing antimalware, antispam, antiphishing, and attachment and content filtering to all active mailboxes, including user, room, equipment, and shared mailboxes.'
      ),
      linkLabel: {
        CLOUD: 'https://www.bitdefender.com/business/support/en/77209-376318-security-for-exchange.html',
        ON_PREMISE: 'https://www.bitdefender.com/business/support/en/77212-376318-security-for-exchange.html',
      },
    },
    [INTEGRATION_TYPES.INTEGRATION_TYPE_SECURITY_TRAINING]: {
      image: 'security-coach-logo.png',
      name: translateFn('SecurityCoach (KnowBe4)'),
      description: translateFn(
        'Allows GravityZone to send event data to the SecurityCoach. The console uses this data to create reports and create real-time coaching campaigns.'
      ),
      linkLabel: {
        CLOUD: 'https://www.bitdefender.com/business/support/en/77211-1198632-securitycoach.html',
        ON_PREMISE: 'https://www.bitdefender.com/business/support/en/77211-1198632-securitycoach.html',
      },
    },
    [INTEGRATION_TYPES.INTEGRATION_TYPE_CONTAINER_IMAGE_SCANNER]: {
      image: 'bitdefender-logo.png',
      name: translateFn('Bitdefender Container Image Scanner'),
      description: translateFn(
        'Connect to GravityZone to sync scan results and display them in the Risk management section.'
      ),
      linkLabel: {
        CLOUD: 'https://www.bitdefender.com/business/support/en/77209-1296428-bitdefender-container-image-scanner.html',
        ON_PREMISE: 'N/A',
      },
    },
  };
}

export const LICENSE_STATUSES_AVAILABLE = {
  NOT_LICENSED: 0,
  AVAILABLE: 1,
  USAGE_LIMIT_REACHED: 7,
};

export const LICENSE_STATUSES_INSTALLED = {
  AVAILABLE: 1,
  STOPPED: 2,
  ACTION_REQUIRED: 3,
  PENDING: 4,
  ACTIVE: 5,
  ERROR: 6,
  USAGE_LIMIT_REACHED: 7,
};

export function getIntegrationData(type: number) {
  const integrationData = getIntegrationsDataSchema();
  const { name, description, image } = integrationData[type];
  const cardType = INTEGRATION_CARD_TYPE[type];
  const linkLabel = Application.Main.product.isCloud()
    ? integrationData[type].linkLabel.CLOUD
    : integrationData[type].linkLabel.ON_PREMISE;

  return {
    name,
    description,
    linkLabel,
    cardType,
    image,
  };
}
